/**
 * Pitch 9-5-2 scoring and match end (rules §2.4–2.6, edge table §4).
 *
 * Eighteen points a hand, in six categories:
 *
 *   High  1  the highest trump in play — to whoever was dealt it
 *   Low   1  the lowest trump in play — to its taker (or holder, by config)
 *   Jack  1  the Jack of trumps — to whoever wins the trick it falls in
 *   Game  1  most game pips captured (A4 K3 Q2 J1 10=10); a tie scores nobody
 *   Five  5  the Five of trumps — to its taker
 *   Nine  9  the Nine of trumps — to its taker
 *
 * A card nobody was dealt scores for nobody. Every dealt card is played (six
 * tricks of four), so "who was dealt it" is simply who played it.
 *
 * The pitcher's side scores what it took if that reaches the bid, and loses
 * the bid if not; the other side always scores what it took.
 */
import { SUIT_LABEL, cardLabel, rankOf, suitOf } from '../../core/cards.js';
export const PITCH_GAME_PIPS = { 14: 4, 13: 3, 12: 2, 11: 1, 10: 10 };
export function gamePips(card) {
    return PITCH_GAME_PIPS[rankOf(card) ?? 0] ?? 0;
}
/** Points a card is worth when captured under this trump, ignoring High and
 *  Low (which depend on what else was dealt). Used by the bot. */
export function capturePoints(card, trump) {
    if (trump === null || suitOf(card) !== trump)
        return 0;
    const r = rankOf(card);
    return r === 9 ? 9 : r === 5 ? 5 : r === 11 ? 1 : 0;
}
function playsOf(hand) {
    const out = [];
    for (const t of hand.tricks) {
        if (t.winner === null)
            continue;
        for (const p of t.plays)
            out.push({ seat: p.seat, card: p.card, winner: t.winner });
    }
    return out;
}
export function pitchAwards(hand, table, config) {
    const trump = hand.extra.trump;
    const contract = hand.contract;
    if (trump === null || !contract)
        return [];
    const plays = playsOf(hand);
    const trumps = plays.filter((p) => suitOf(p.card) === trump);
    const byRank = (r) => trumps.find((p) => rankOf(p.card) === r) ?? null;
    const team = (s) => table.teamOf[s];
    const awards = [];
    const t = SUIT_LABEL[trump];
    if (trumps.length > 0) {
        const high = trumps.reduce((a, b) => ((rankOf(b.card) ?? 0) > (rankOf(a.card) ?? 0) ? b : a));
        awards.push({ kind: 'high', points: 1, team: team(high.seat), card: high.card, text: `High (${cardLabel(high.card)})` });
        const low = trumps.reduce((a, b) => ((rankOf(b.card) ?? 99) < (rankOf(a.card) ?? 99) ? b : a));
        const lowSeat = config.lowGoesTo === 'holder' ? low.seat : low.winner;
        awards.push({ kind: 'low', points: 1, team: team(lowSeat), card: low.card, text: `Low (${cardLabel(low.card)})` });
    }
    else {
        awards.push({ kind: 'high', points: 1, team: null, card: null, text: `High — no ${t} in play` });
        awards.push({ kind: 'low', points: 1, team: null, card: null, text: `Low — no ${t} in play` });
    }
    const special = (kind, rank, points, name) => {
        const p = byRank(rank);
        awards.push(p
            ? { kind, points, team: team(p.winner), card: p.card, text: `${name} (${cardLabel(p.card)})` }
            : { kind, points, team: null, card: null, text: `${name} — not dealt` });
    };
    special('jack', 11, 1, 'Jack');
    const pips = table.teams.map(() => 0);
    for (const p of plays)
        pips[team(p.winner)] += gamePips(p.card);
    const best = Math.max(...pips);
    const leaders = table.teams.filter((tm) => pips[tm.id] === best).map((tm) => tm.id);
    const pitcherTeam = team(contract.pitcher);
    let gameTeam = leaders.length === 1 ? leaders[0] : null;
    if (gameTeam === null && config.gameTie === 'pitcher' && leaders.includes(pitcherTeam))
        gameTeam = pitcherTeam;
    awards.push({
        kind: 'game',
        points: 1,
        team: gameTeam,
        card: null,
        text: gameTeam === null
            ? `Game — tied on ${best}`
            : `Game (${pips[gameTeam]} to ${Math.max(...pips.filter((_, i) => i !== gameTeam), 0)})`,
    });
    special('five', 5, 5, 'Five');
    special('nine', 9, 9, 'Nine');
    return awards;
}
export function pitchTricksBySeat(hand, seatCount) {
    const out = Array.from({ length: seatCount }, () => 0);
    for (const t of hand.tricks)
        if (t.winner !== null)
            out[t.winner] += 1;
    return out;
}
export function pitchScoreHand(hand, table, config) {
    const bySeat = pitchTricksBySeat(hand, table.seatCount);
    const contract = hand.contract;
    const deltas = table.teams.map(() => 0);
    if (!contract) {
        return { deltas, tricksBySeat: bySeat, contractMade: null, lines: [], matchExtra: { lastBid: null } };
    }
    const awards = pitchAwards(hand, table, config);
    const taken = table.teams.map(() => 0);
    for (const a of awards)
        if (a.team !== null)
            taken[a.team] += a.points;
    const pitcherTeam = table.teamOf[contract.pitcher];
    const made = taken[pitcherTeam] >= contract.bid;
    // Each team's lines sum to its delta, so a set side shows one line for the
    // setback rather than the points it took and then lost.
    const lines = [];
    for (const tm of table.teams) {
        if (tm.id === pitcherTeam && !made) {
            deltas[tm.id] = -contract.bid;
            lines.push({ team: tm.id, text: `Set back — took ${taken[tm.id]} of ${contract.bid}`, points: -contract.bid });
            continue;
        }
        deltas[tm.id] = taken[tm.id];
        for (const a of awards)
            if (a.team === tm.id)
                lines.push({ team: tm.id, text: a.text, points: a.points });
    }
    return {
        deltas,
        tricksBySeat: bySeat,
        contractMade: made,
        lines,
        matchExtra: { lastBid: { pitcher: contract.pitcher, team: pitcherTeam, bid: contract.bid, made } },
    };
}
/**
 * First side to the target wins; if both get there on the same hand, the
 * pitcher's side counts first and wins (rules §2.6, edge table).
 */
export function pitchMatchEnd(state, config) {
    const scores = state.scores;
    const last = state.extra?.lastBid ?? null;
    if (last && (scores[last.team] ?? 0) >= config.target) {
        return { finished: true, winningTeam: last.team, reason: `Pitched ${last.bid} and reached ${scores[last.team]}` };
    }
    const over = state.table.teams.filter((t) => (scores[t.id] ?? 0) >= config.target);
    if (over.length === 0)
        return { finished: false };
    const best = over.reduce((a, b) => ((scores[b.id] ?? 0) > (scores[a.id] ?? 0) ? b : a));
    const tied = over.filter((t) => scores[t.id] === scores[best.id]).length > 1;
    return {
        finished: true,
        winningTeam: tied ? null : best.id,
        reason: `Reached ${scores[best.id]}`,
    };
}
//# sourceMappingURL=scoring.js.map